import { useEffect, useState } from "react";
import { Alert, Form, Container, FormSelect, Row, Col, Modal, Button } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { t } from "../locale";
import NationalityDropdown from "../components/NationalityDropdown";
import StepGuide from "../components/StepGuide";
import { formatDate, parseDate, updateStatus } from "../helpers/helpers";
import DatePicker from "react-datepicker";
import "react-datepicker/src/stylesheets/datepicker.scss";
import 'bootstrap/dist/css/bootstrap.min.css';
import { isMobile } from "react-device-detect";
import PrivacyPolicyModal from '../components/PrivacyPolicyModal';
import { STATUS_INITIATED } from "../utils/constants";
import { mapBackendNationality } from "../helpers/mapNationality";
import VerificationStepNav from "../components/VerificationStepNav";

// must match the "United States" value NationalityDropdown assigns to its US option
const USA_NATIONALITY_VALUE = "United States";

export const Index = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hasAccess, setHasAccess] = useState(true);
  const [expiredPage, setExpiredPage] = useState(false);
  const [space, setSpace] = useState('mb-5')
  const [spaceHeight, setSpaceHeight] = useState('mt-5')
  const [showModal, setShowModal] = useState(false);
  const [privacyPolicy, setPrivacyPolicy] = useState(false)
  const [showUsaNotice, setShowUsaNotice] = useState(false);
  const [invalidFields, setInvalidFields] = useState<string[]>(
    (location.state as { invalidFields?: string[] } | null)?.invalidFields ?? []
  );

  const clearInvalidField = (field: string) => {
    setInvalidFields((prev) => prev.filter((f) => f !== field));
  };

  const fieldBorderClass = (field: string) =>
    invalidFields.includes(field) ? 'border-warning border-2' : 'border-primary';

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const sessionId = urlParams.get("session_id");

  const pathSegments = window.location.pathname.split("/");
  const companyId = pathSegments[1];
  const requestId = pathSegments[2];

  const currentPath = window.location.pathname;
  let navigationPath: boolean;
  if (currentPath.endsWith("/p")) {
    navigationPath = true;
  } else {
    navigationPath = false;
  }

  const { mobileNav, desktopNav } = VerificationStepNav({
    currentStep: 1,
    companyId,
    requestId,
    onBack: undefined,
    spaceHeight
  })

  useEffect(() => {
    fetchData()
    updateStatus(companyId, requestId, STATUS_INITIATED, 'overview')

    if (isMobile) {
      setSpace('')
      setSpaceHeight('')
    }

    checkPrivacyPolicy()
  }, []);

  const [userData, setUserData] = useState({
    gender: "",
    firstName: "",
    lastName: "",
    email: "",
    dateOfBirth: "",
    cityOfBirth: "",
    nationality: "",
    status: "",
    language: "",
    idNumber: "",
    passport: ""
  });

  const goToNextPage = ($url: string) => {
    navigate($url);
  }
  const checkPrivacyPolicy = async () => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/request/data/all`,
      {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          companyId: companyId,
          requestId: requestId
        })
      }
    )

    const data = await response.json();

    if (data.privacyPolicy === true) {
      setShowModal(false)
    } else {
      setShowModal(true)
    }
  }

  const handleAccept = async () => {

    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/request/update`,
      {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          companyId: companyId,
          requestId: requestId,
          userData: { privacyPolicy: true }
        })
      }

    )
    if (!response.ok) {
      throw new Error("Failed to fetch data");
    }
    setShowModal(false);
    window.location.reload();
  };

  const handleClose = () => {
    setShowModal(false);
    window.location.reload();
  };

  const fetchData = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/request/data/all`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            companyId: companyId,
            requestId: requestId,
          }),
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      const data = await response.json();

      if (data.status === "EXPIRED") {
        setExpiredPage(false);
      } else {
        setUserData(data);
      }

      if (data.privacyPolicy === true)
        setPrivacyPolicy(true)

    } catch (error) {
      console.error("Error fetching data:", error);
      setHasAccess(false);
    }
  };

  const handleSubmit = async (event: any) => {

    event.preventDefault();

    if (userData.nationality === USA_NATIONALITY_VALUE && userData.passport !== "true") {
      setUserData({ ...userData, passport: "true" });
      setShowUsaNotice(true);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/request/update`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            companyId: companyId,
            requestId: requestId,
            userData,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to submit data");
      }

      if (!navigationPath) {
        goToNextPage(`/${companyId}/${requestId}/return/?session_id=${sessionId}`);
      } else {
        goToNextPage(`/${companyId}/${requestId}/si/${userData.passport}/p`);
      }
    } catch (error) {
      console.error("Error submitting data:", error);
    }
  };

  return (
    <>
      <div className="App">
        {/* Modal is displayed when showModal is true */}
        <PrivacyPolicyModal show={showModal} onClose={handleClose} onAccept={handleAccept} />
      </div>
      <Modal show={showUsaNotice} onHide={() => setShowUsaNotice(false)} centered>
        <Modal.Header closeButton className="bg-warning">
          <Modal.Title>⚠ {t('notice')}</Modal.Title>
        </Modal.Header>
        <Modal.Body className=" fw-semibold">{t('usaPassportRequiredNotice')}</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowUsaNotice(false)}>
            {t('ok')}
          </Button>
        </Modal.Footer>
      </Modal>
      {mobileNav}
      {hasAccess ? (
        <Container hidden={expiredPage} className={`container overflow-hidden ${spaceHeight}`}>
          <Row className="row overflow-auto">
            {desktopNav}
            <div className={`col d-flex flex-column h-sm-100 px-sm-5 `}>
              <main className={`row overflow-auto px-sm-5`}>
                <div className={`col mt-n1 pt-4 pt-sm-5`}>
                  <h4 className="mb-4 text-start">{t('titleDataForm')}</h4>
                  {invalidFields.length > 0 && (
                    <Alert variant="warning" className="text-start">
                      {t('fieldMismatchNotice')}
                    </Alert>
                  )}
                  <Form className="needs-validation" onSubmit={handleSubmit}>
                    <div className="row gx-2">
                      <div className={`row gx-2 ${space}`}>
                        <div className="col-sm-4"  style={{ position: 'relative' }}>
                          <StepGuide
                            step={undefined}
                            text={t('guideTypeGender')}
                            show={!userData.gender}
                            arrowAlign="left"
                            floating
                          />
                          <div className="form-floating mb-2">
                            <FormSelect
                              className={fieldBorderClass('gender')}
                              id='gender'
                              value={userData.gender}
                              required
                              onChange={(e) => {
                                setUserData({
                                  ...userData,
                                  gender: e.target.value,
                                })
                                clearInvalidField('gender')
                              }}
                            >
                              <option value=''>--</option>
                              <option value='M'>{t('male')}</option>
                              <option value='F'>{t('female')}</option>
                              <option value='B'>{t('other')}</option>
                            </FormSelect>
                            <label
                              htmlFor="salutation">{t('gender')} <span className="text-danger">*</span></label>

                          </div>
                        </div>

                        <div className="col-sm-4" style={{ position: 'relative' }}>
                          <StepGuide
                            step={undefined}
                            text={t('guideTypeFirstName')}
                            show={!userData.firstName}
                            arrowAlign="left"
                            floating
                          />
                          <div className="form-floating mb-2">
                            <Form.Control
                              className={fieldBorderClass('firstName')}
                              type="text"
                              value={userData.firstName}
                              placeholder="First name"
                              required
                              onChange={(e) => {
                                setUserData({
                                  ...userData,
                                  firstName: e.target.value,
                                })
                                clearInvalidField('firstName')
                              }}
                            />
                            <label htmlFor="firstName">{t('firstName')} <span className="text-danger">*</span></label>
                          </div>
                        </div>

                        <div className="col-sm-4"  style={{ position: 'relative' }}>
                          <StepGuide
                            step={undefined}
                            text={t('guideTypeLastName')}
                            show={!userData.lastName}
                            arrowAlign="left"
                            floating
                          />
                          <div className="form-floating mb-2">
                            <Form.Control
                              className={fieldBorderClass('lastName')}
                              type="text"
                              value={userData.lastName}
                              required
                              onChange={(e) => {
                                setUserData({
                                  ...userData,
                                  lastName: e.target.value,
                                })
                                clearInvalidField('lastName')
                              }}
                            />
                            <label htmlFor="lastName">{t('lastName')} <span className="text-danger">*</span></label>
                          </div>
                        </div>
                      </div>

                      <div className={`row gx-2 ${space}`}>
                        <div className="col-sm-4" style={{ position: 'relative' }}>
                          <StepGuide
                            step={undefined}
                            text={t('guideTypeEmail')}
                            show={!userData.email}
                            arrowAlign="left"
                            floating
                          />
                          <div className="form-floating mb-2">
                            <Form.Control
                              className='border-primary'
                              type="email"
                              value={userData.email}
                              required
                              readOnly
                              onChange={(e) =>
                                setUserData({
                                  ...userData,
                                  email: e.target.value,
                                })
                              }
                            />
                            <label htmlFor="email">{(t('email'))} <span className="text-danger">*</span></label>
                          </div>
                        </div>

                        <div className="col-sm-4" style={{ position: 'relative' }}>

                          <StepGuide
                            step={undefined}
                            text={t('guideSelectDateOfBirth')}
                            show={!userData.dateOfBirth}
                            arrowAlign="left"
                            floating
                          />
                          <div className="form-floating mb-2">
                            <label style={{ fontSize: '12px', transform: 'translateY(-12px)', zIndex: 2 }} htmlFor="birthday">{t('dateOfBirth')} <span className="text-danger">*</span></label>
                            <DatePicker
                              className={`form-control ${fieldBorderClass('dateOfBirth')}`}
                              selected={parseDate(userData.dateOfBirth)}
                              required
                              dateFormat="dd.MM.yyyy"
                              // placeholderText="dd.MM.yyyy"
                              showYearDropdown
                              showMonthDropdown
                              dropdownMode="select"
                              onChange={(date: Date | null) => {
                                setUserData({
                                  ...userData,
                                  dateOfBirth: formatDate(date).toString(),
                                });
                                clearInvalidField('dateOfBirth')
                              }}
                              onChangeRaw={(e) => {
                                if (e?.type !== 'blur') return;
                                const raw = (e?.target as HTMLInputElement)?.value ?? '';
                                const parsed = parseDate(raw);
                                if (parsed) {
                                  setUserData({
                                    ...userData,
                                    dateOfBirth: formatDate(parsed).toString(),
                                  });
                                  clearInvalidField('dateOfBirth')
                                }
                              }}
                              customInput={
                                <input
                                  className={`form-control ${fieldBorderClass('dateOfBirth')}`}
                                  style={{ paddingTop: '1.5rem' }}
                                />
                              }
                            />
                          </div>
                        </div>

                        <div className='col-sm-4' style={{ position: 'relative' }}>
                          <StepGuide
                            step={undefined}
                            text={t('guideSelectDocumentFirst')}
                            show={!userData.passport}
                            arrowAlign="left"
                            floating
                          />
                          <div className="form-floating mb-2">
                            <select className="form-control border-primary"
                              id="documentType"
                              required={true}
                              value={userData.passport}
                              onChange={(e) => {
                                if (e.target.value !== "true" && userData.nationality === USA_NATIONALITY_VALUE) {
                                  setShowUsaNotice(true);
                                  return;
                                }
                                setUserData({
                                  ...userData,
                                  passport: e.target.value,
                                })
                              }}>
                              <option value=""></option>
                              <option value="false">{t('idCard')}</option>
                              <option
                                value="true">{t('passport')}</option>
                            </select>
                            <label htmlFor="documentType">{t('documentType')} <span className="text-danger">*</span></label>
                          </div>
                        </div>

                      </div>

                      <div className={`row gx-2 ${space}`}>
                        <div className='col-sm-4' style={{ position: 'relative' }}>

                          <StepGuide
                            step={undefined}
                            text={t('guideSelectNationality')}
                            show={!userData.nationality}
                            arrowAlign="left"
                            floating
                          />
                          <div className="form-floating mb-2">

                            <NationalityDropdown
                              disabled={!userData.passport}
                              invalid={invalidFields.includes('nationality')}
                              selectedNationality={mapBackendNationality(userData.nationality) || ''}
                              passport={userData.passport}
                              onChange={(e: any) => {
                                const nationality = e.target.value;
                                const requiresPassport = nationality === USA_NATIONALITY_VALUE && userData.passport !== "true";
                                setUserData({
                                  ...userData,
                                  nationality,
                                  passport: requiresPassport ? "true" : userData.passport,
                                })
                                clearInvalidField('nationality')
                                if (requiresPassport) {
                                  setShowUsaNotice(true);
                                }
                              }}
                            />
                            <label
                              htmlFor="nationality">{t('nationality')}<span className="text-danger">*</span></label>
                          </div>
                        </div>

                        <div className='col-sm-4' style={{ position: 'relative' }}>
                          <StepGuide
                            step={undefined}
                            text={t('guideTypeIdNumber')}
                            show={!userData.idNumber}
                            arrowAlign="left"
                            floating
                          />
                          <div className="form-floating mb-2">
                            <Form.Control
                              className={fieldBorderClass('idNumber')}
                              value={userData.idNumber}
                              required
                              onChange={(e: any) => {
                                setUserData({
                                  ...userData,
                                  idNumber: e.target.value
                                })
                                clearInvalidField('idNumber')
                              }}
                            />
                            <label htmlFor="documentNumber">{t('idNumber')} <span className="text-danger">*</span></label>
                          </div>
                        </div>
                      </div>

                      <hr className="my-5" />

                      <div className="col-sm-4 offset-sm-8 text-end">
                        {privacyPolicy && <button
                          className="btn btn-success btn-lg text-black"
                          type="submit">{t('nextStep')} »</button>}
                      </div>
                    </div>
                  </Form>
                  <p className="my-5">&nbsp;</p>
                </div>
              </main>
            </div>
          </Row >
        </Container >
      ) : (
        <Alert variant="danger" className="mt-3">
          <div>{t("accessDenied")}</div>
        </Alert>


      )}
      <div hidden={!expiredPage}>
        <h2>{t("requestExpired")}</h2>
        <p>{t("accessExpired")}</p>
      </div>
    </>

  );
};

export default Index;
